import {
    AuditGroupLegalEntityRowApiModel,
    AuditGroupLegalEntityRowViewModel,
    LatestAuditSubmissionFilterOptions,
    LegalEntityAuditGroupSelectableSystem,
    LegalEntityAuditInformation,
    LegalEntityAuditInformationViewModel,
    LegalEntityClassificationInformationViewModel,
} from "./AuditGroupLegalEntitySelector.types";
import { useState } from "react";
import { IAuditGroupLegalEntitySelector } from "./AuditGroupLegalEntitySelector";
import { LegalEntityAuditGroupDraft } from "../LegalEntityAuditGroupOverview.types";
import { useTranslation } from "../../../localization/useTranslation";
import { DotLegalSelectOption } from "../../../common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { LegalEntityAuditInformationsDto } from "../../../legalEntity/legalEntities/LegalEntities.types";
import { setFirstLetterToLowerCase } from "../../../common/stringOperations";
import { RiskLevel } from "../../../legalEntity/legalEntityClassificationTab/LegalEntityClassificationTab.types";
import { LegalEntityAuditFrequency, LegalEntityAuditType } from "../../../legalEntity/LegalEntity.types";
import { useFilterState } from "@common/hooks/useFilterState";
import { useStateSessionStorage } from "@common/hooks/useStateSessionStorage";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching";
import { GetListData } from "@common/components/filterPopover/subMenues/SubMenuDataHelper";
import { getEnumValues } from "../../../common/enumOperations";
import { useLegalEntityTypeNames } from "../../../legalEntity/useLegalEntityTypeNames.ts";

import { useDotLegalQuery, useDotLegalPostGetQuery } from "@common/hooks/dotLegalReactQuery";
import { usePanes, useRefetchWhenPaneSaves } from "../../../main/panes/usePanes";
import LegalEntityContactPane from "../../../legalEntity/legalEntityContactTab/legalEntityContactPane/LegalEntityContactPane";
import { withoutContact } from "../../../legalEntity/legalEntityAuditTab/auditAdditionalRecipients.utils";

const filterIds = {
    type: "type",
    country: "countryCodes",
    classification: "classification",
    riskLevel: "riskLevel",
    audit: "audit",
    latestAuditSubmission: "latestAuditSubmission",
    supervisionType: "supervisionType",
    supervisionFrequency: "supervisionFrequency",
};

const parentFilterIdByDependentId: Record<string, string> = {
    [filterIds.riskLevel]: filterIds.classification,
    [filterIds.latestAuditSubmission]: filterIds.audit,
};

export default function useAuditGroupLegalEntitySelectorHooks(props: IAuditGroupLegalEntitySelector) {
    const { getLegalEntityTypeName } = useLegalEntityTypeNames();
    const { translateCountry, translateString } = useTranslation();
    const { open: openPane } = usePanes();
    useRefetchWhenPaneSaves(props.onContactCreated);
    const [showSendAuditsDialog, setShowSendAuditsDialog] = useState(false);
    const [sendClicked, setSendClicked] = useState(false);

    // Keyed per group: "select all" only picks the rows left after filtering, so filters carried over from another
    // draft would silently narrow who this group is sent to.
    const filterStateKey = `audit-group-draft-legal-entities-${props.auditGroupId}`;
    const { selectFilters, filterOperators, onFilterChange: storeFilters, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState(filterStateKey);
    const [searchString, setSearchString] = useStateSessionStorage<string>(`${filterStateKey}-search`, "");
    const searchedRiskLevel = selectFilters[filterIds.riskLevel] ?? [];
    const searchedClassification = selectFilters[filterIds.classification]?.[0] ?? "";
    const searchedAudit = selectFilters[filterIds.audit]?.[0] ?? "";
    const searchedLatestAuditSubmission = selectFilters[filterIds.latestAuditSubmission]?.[0] ?? "";

    const auditInformationsUrl = `/legalEntityAudit/auditInformations`;
    let auditInformationsQuery = useDotLegalPostGetQuery<Array<LegalEntityAuditInformation>>(
        auditInformationsUrl,
        {
            legalEntityIds: props.rows.map((x) => x.id),
            fetchLatestAudit: true,
        } as LegalEntityAuditInformationsDto,
        {
            enabled: !props.selectedEntititesIsLoading,
        }
    );

    if (auditInformationsQuery.data) {
        auditInformationsQuery.data.forEach((s) => {
            var entity = props.rows.find((x) => x.id === s.legalEntityId);

            if (entity) {
                entity.classifications = s.classifications;
                entity.latestAudit = s.latestAudit;
                entity.audits = s.audits;
            }
        });
    }

    const selectableSystemsUrl = `/legalEntityAuditGroups/systems`;
    let selectableSystemsQuery = useDotLegalQuery<Array<LegalEntityAuditGroupSelectableSystem>>(selectableSystemsUrl, {
        enabled: !props.selectedEntititesIsLoading,
    });

    if (selectableSystemsQuery.data) {
        selectableSystemsQuery.data.forEach((s) => {
            var entity = props.rows.find((x) => x.id === s.legalEntityId);

            if (entity) {
                entity.assets = s.selectableSystems;
            }
        });
    }

    const filters = {
        countrySelectOptions: Array<DotLegalSelectOption>(),
        typeSelectOptions: Array<DotLegalSelectOption>(),
        auditTemplateOptions: Array<DotLegalSelectOption>(),
        latestAuditSubmissionFilterOptions: Array<DotLegalSelectOption>(),
        superVisionTypeOptions: Array<DotLegalSelectOption>(),
        superVisionFrequencyOptions: Array<DotLegalSelectOption>(),
        riskLevelOptions: Array<DotLegalSelectOption>(),
        classificationOptions: Array<DotLegalSelectOption>(),
    };

    let rows: Array<AuditGroupLegalEntityRowApiModel> | undefined = undefined;
    if (props.rows.length > 0) {
        rows = [...props.rows];

        rows.forEach((x) => {
            createCountrySelectOptions(x.countryCode);
            createTypeOptions(x.legalEntityTypes);
            createLatestAuditFilterOptions(x.audits ?? []);
            createAuditTypeFilterOptions(x.auditType);
            createAuditFrequencyFilterOptions(x.auditFrequency);
            createClassificationFilterOptions(x.classifications ?? []);
        });

        createRiskLevelSelectOptions();
        createLastSubmittedDateSelectOptions();
        onSelectClassification(searchedClassification);

        sortSelectOptions();

        rows = getFilteredRows(rows);
    }

    const onCheckBoxClick = (row: AuditGroupLegalEntityRowApiModel) => {
        let temp = [...props.selectedEntities];
        if (temp.some((x) => x.legalEntityId === row.id)) {
            temp = temp.filter((x) => x.legalEntityId !== row.id);
        } else {
            var contact = row.contacts.find((x) => x.isPrimaryContact);
            temp.push(new LegalEntityAuditGroupDraft(row.id, contact?.id, [], []));
        }

        props.onSelectedEntitiesUpdated(temp);
    };

    const onSelectAllEntities = (selected: Array<AuditGroupLegalEntityRowViewModel>) => {
        let selectedDrafts = selected.map((s) => {
            const contact = props.rows.find((r) => r.id === s.id)?.contacts.find((x) => x.isPrimaryContact);
            return new LegalEntityAuditGroupDraft(s.id, contact?.id, [], []);
        });

        props.onSelectedEntitiesUpdated(selectedDrafts);
    };

    function openAddContactPane(legalEntityId: string) {
        openPane(LegalEntityContactPane, { legalEntityId, auditGroupId: props.auditGroupId });
    }

    const updateDraft = (rowId: string, update: (draft: LegalEntityAuditGroupDraft) => void) => {
        let temp = [...props.selectedEntities];

        let entity = temp.find((x) => x.legalEntityId == rowId);

        if (entity) {
            update(entity);
        }

        props.onSelectedEntitiesUpdated(temp);
    };

    const onContactChange = (rowId: string, contactId: string) => {
        updateDraft(rowId, (draft) => {
            draft.contactId = contactId;
            draft.additionalRecipientIds = withoutContact(draft.additionalRecipientIds, contactId);
        });
    };

    const onAdditionalRecipientsChange = (rowId: string, additionalRecipientIds: Array<string>) => {
        updateDraft(rowId, (draft) => {
            draft.additionalRecipientIds = additionalRecipientIds;
        });
    };

    const onSystemChange = (rowId: string, systemIds: Array<string>) => {
        updateDraft(rowId, (draft) => {
            draft.systemIds = systemIds;
        });
    };

    const setHeaderCheckBoxState = () => {
        let allSelected = rows?.every(function (item) {
            return props.selectedEntities.some((x) => x.legalEntityId === item.id);
        });

        return props.selectedEntities?.length === 0 ? "false" : allSelected ? "true" : "indeterminate";
    };

    // The risk level narrows the chosen classification and the submission narrows the chosen audit, so each is only
    // offered once its parent is chosen and goes with it when the parent is removed.
    function getFilterOptions(): Array<FilterOption> {
        const options: Array<FilterOption> = [
            {
                id: filterIds.type,
                label: translateString("type"),
                type: FilterOptionType.SelectField,
                value: selectFilters[filterIds.type] ?? [],
                fixedData: filters.typeSelectOptions,
                singleSelect: true,
            },
            {
                id: filterIds.country,
                label: translateString("countriesPlural"),
                type: FilterOptionType.SelectField,
                value: selectFilters[filterIds.country] ?? [],
                fixedData: filters.countrySelectOptions,
                singleSelect: true,
            },
            {
                id: filterIds.classification,
                label: translateString("auditGroupClassification"),
                type: FilterOptionType.SelectField,
                value: selectFilters[filterIds.classification] ?? [],
                fixedData: filters.classificationOptions,
                singleSelect: true,
                hideFilterOperator: true,
            },
        ];

        if (searchedClassification) {
            options.push({
                id: filterIds.riskLevel,
                label: translateString("classificationRiskLevel"),
                type: FilterOptionType.SelectField,
                value: searchedRiskLevel,
                fixedData: filters.riskLevelOptions,
                hideFilterOperator: true,
            });
        }

        options.push({
            id: filterIds.audit,
            label: translateString("legalEntitiesLatestAuditHeader"),
            type: FilterOptionType.SelectField,
            value: selectFilters[filterIds.audit] ?? [],
            fixedData: filters.auditTemplateOptions,
            singleSelect: true,
            hideFilterOperator: true,
        });

        if (searchedAudit) {
            options.push({
                id: filterIds.latestAuditSubmission,
                label: translateString("latestAuditSubmission"),
                type: FilterOptionType.SelectField,
                value: selectFilters[filterIds.latestAuditSubmission] ?? [],
                fixedData: filters.latestAuditSubmissionFilterOptions,
                singleSelect: true,
                hideFilterOperator: true,
            });
        }

        options.push(
            {
                id: filterIds.supervisionType,
                label: translateString("legalEntityAuditType"),
                type: FilterOptionType.SelectField,
                value: selectFilters[filterIds.supervisionType] ?? [],
                fixedData: filters.superVisionTypeOptions,
            },
            {
                id: filterIds.supervisionFrequency,
                label: translateString("legalEntityFrequency"),
                type: FilterOptionType.SelectField,
                value: selectFilters[filterIds.supervisionFrequency] ?? [],
                fixedData: filters.superVisionFrequencyOptions,
            }
        );

        return withOperators(options, filterOperators);
    }

    function onFilterChange(options: Array<FilterOption>) {
        storeFilters(options.filter((option) => !isOrphanedDependentFilter(option, options)));
    }

    return {
        getFilterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        searchString,
        setSearchString,
        searchedAudit,
        systemsIsLoading: selectableSystemsQuery.isLoading,
        auditIsLoading: auditInformationsQuery.isLoading,
        openAddContactPane,
        onCheckBoxClick,
        setHeaderCheckBoxState,
        onSelectAllEntities,
        onContactChange,
        onAdditionalRecipientsChange,
        onSystemChange,
        showSendAuditsDialog,
        setShowSendAuditsDialog,
        sendClicked,
        setSendClicked,
        rows,
    };

    function isOrphanedDependentFilter(option: FilterOption, options: Array<FilterOption>) {
        const parentId = parentFilterIdByDependentId[option.id];
        if (!parentId) {
            return false;
        }

        const parent = options.find((candidate) => candidate.id === parentId);
        return !parent || GetListData(parent.value).length === 0;
    }

    function getFilteredRows(rows: Array<AuditGroupLegalEntityRowApiModel>): Array<AuditGroupLegalEntityRowApiModel> {
        let tempData = applyClientFilters(
            rows,
            [
                { id: filterIds.type, candidate: (row: AuditGroupLegalEntityRowApiModel) => row.legalEntityTypes.map((type) => type.toString()) },
                { id: filterIds.country, candidate: (row: AuditGroupLegalEntityRowApiModel) => row.countryCode.toLowerCase() },
                { id: filterIds.supervisionType, candidate: (row: AuditGroupLegalEntityRowApiModel) => row.auditType?.toString() },
                { id: filterIds.supervisionFrequency, candidate: (row: AuditGroupLegalEntityRowApiModel) => row.auditFrequency?.toString() },
            ],
            selectFilters,
            {},
            filterOperators
        )!;

        if (searchedClassification) {
            tempData = tempData.filter(
                (row) => row.classifications?.filter((classification) => searchedClassification.indexOf(classification.questionnaireId) >= 0).length > 0
            );
        }

        if (searchedRiskLevel.length > 0 && searchedRiskLevel[0] !== "") {
            tempData.forEach(
                (row) =>
                    (row.classifications = row.classifications.filter(
                        (classification) =>
                            (classification && searchedRiskLevel.indexOf(classification.riskLevel?.toString() ?? "") >= 0) ||
                            (searchedRiskLevel.includes("null") && classification.riskLevel === null)
                    ))
            );

            tempData = tempData.filter((x) => x.classifications.length > 0);
        }

        if (searchedAudit) {
            tempData = tempData.filter((row) => row.audits?.filter((audit) => searchedAudit.indexOf(audit.questionnaireId) >= 0).length > 0);
        }

        if (searchedLatestAuditSubmission) {
            var submissionRange = getSubmissionRange();

            tempData = tempData.filter((row) => {
                if ([row.audits?.length ?? 0].length > 0) {
                    let audit = row.audits.find((x) => x.questionnaireId === searchedAudit)!;

                    if (audit.isClosed) {
                        return searchedLatestAuditSubmission.indexOf("isclosed") >= 0;
                    }

                    if (audit.submissionDate) {
                        if (submissionRange) {
                            return submissionRange > new Date(audit.submissionDate);
                        }
                    } else {
                        return searchedLatestAuditSubmission.indexOf("notsubmitted") >= 0;
                    }
                }

                return false;
            });
        }

        return tempData;
    }

    function getSubmissionRange() {
        var today = new Date();

        switch (searchedLatestAuditSubmission) {
            case "0":
                today.setMonth(today.getMonth() - 1);
                break;
            case "1":
                today.setMonth(today.getMonth() - 3);
                break;
            case "2":
                today.setMonth(today.getMonth() - 6);
                break;
            case "3":
                today.setFullYear(today.getFullYear() - 1);
                break;
            case "4":
                today.setFullYear(today.getFullYear() - 2);
                break;
            case "5":
                today.setFullYear(today.getFullYear() - 3);
                break;
            default:
                return undefined;
        }

        return today;
    }

    function createCountrySelectOptions(countryCode: string) {
        if (!filters.countrySelectOptions.some((x) => x.id === countryCode.toLowerCase())) {
            filters.countrySelectOptions.push({ name: translateCountry(countryCode), id: countryCode.toLowerCase() });
        }
    }

    function createTypeOptions(types: Array<string>) {
        for (let type of types) {
            if (!filters.typeSelectOptions.some((x) => x.id === type)) {
                filters.typeSelectOptions.push({
                    id: type,
                    name: getLegalEntityTypeName(type),
                });
            }
        }
    }

    function createClassificationFilterOptions(classifications: Array<LegalEntityClassificationInformationViewModel>) {
        classifications.forEach((classification) => {
            if (!filters.classificationOptions.some((x) => x.id == classification.questionnaireId)) {
                filters.classificationOptions.push({ id: classification.questionnaireId, name: classification.name });
            }
        });
    }

    function createRiskLevelSelectOptions() {
        if (auditInformationsQuery.data) {
            let riskLevels = props.rows
                .flatMap((x) => x.classifications)
                .filter((x) => x && x.questionnaireId === searchedClassification)
                .map((x) => x.riskLevel);

            riskLevels.forEach((level) => {
                if (level !== null && level !== undefined && !filters.riskLevelOptions.some((x) => x.id === level?.toString())) {
                    filters.riskLevelOptions.push({
                        id: level.toString(),
                        name: translateString(setFirstLetterToLowerCase(RiskLevel[level])),
                    });
                }
            });

            if (riskLevels.some((x) => x === null)) {
                filters.riskLevelOptions.push({ id: "null", name: translateString("notAnswered") });
            }
        }

        filters.riskLevelOptions.sort((a, b) => (a.id > b.id ? 1 : -1));
    }

    function createLastSubmittedDateSelectOptions() {
        if (auditInformationsQuery.data && searchedAudit) {
            let audits = props.rows?.flatMap((x) => x.audits).filter((x) => x.questionnaireId === searchedAudit);

            if (audits?.some((a) => a.submissionDate !== null)) {
                filters.latestAuditSubmissionFilterOptions = getEnumValues(LatestAuditSubmissionFilterOptions).map((option) => {
                    return {
                        name: translateString(setFirstLetterToLowerCase(LatestAuditSubmissionFilterOptions[option].toString())),
                        id: option.toString(),
                    };
                });
            }

            if (audits?.some((a) => a.isClosed) && !filters.latestAuditSubmissionFilterOptions.some((x) => x.id === "isclosed")) {
                filters.latestAuditSubmissionFilterOptions.push({ id: "isclosed", name: translateString("excluded") });
            }

            if (audits?.some((a) => a.submissionDate === null) && !filters.latestAuditSubmissionFilterOptions.some((x) => x.id === "notsubmitted")) {
                filters.latestAuditSubmissionFilterOptions.push({
                    id: "notsubmitted",
                    name: translateString("notSubmitted"),
                });
            }
        }
    }

    function createLatestAuditFilterOptions(audits: Array<LegalEntityAuditInformationViewModel>) {
        audits.forEach((audit) => {
            if (!filters.auditTemplateOptions.some((x) => x.id == audit.questionnaireId)) {
                filters.auditTemplateOptions.push({ id: audit.questionnaireId, name: audit.name });
            }
        });
    }

    function createAuditTypeFilterOptions(auditType?: LegalEntityAuditType) {
        if (auditType && !filters.superVisionTypeOptions.some((x) => x.id === auditType.toString())) {
            filters.superVisionTypeOptions.push({
                name: translateString(setFirstLetterToLowerCase(LegalEntityAuditType[auditType].toString())),
                id: auditType.toString(),
            });
        }
    }

    function createAuditFrequencyFilterOptions(auditFrequency?: LegalEntityAuditFrequency) {
        if (auditFrequency && !filters.superVisionFrequencyOptions.some((x) => x.id === auditFrequency.toString())) {
            filters.superVisionFrequencyOptions.push({
                name: translateString(setFirstLetterToLowerCase(LegalEntityAuditFrequency[auditFrequency].toString())),
                id: auditFrequency.toString(),
            });
        }
    }

    function onSelectClassification(classificationId: string | null) {
        if (auditInformationsQuery.data) {
            if (classificationId) {
                props.rows.forEach((x) => (x.classifications = x.classifications.filter((x) => x.questionnaireId === classificationId)));
            }
        }
    }

    function sortSelectOptions() {
        filters.countrySelectOptions.sort((a, b) => (a.id > b.id ? 1 : -1));
        filters.typeSelectOptions.sort((a, b) => a.name.localeCompare(b.name));
        filters.classificationOptions.sort((a, b) => (a.name > b.name ? 1 : -1));

        filters.auditTemplateOptions.sort((a, b) => (a.name > b.name ? 1 : -1));
        filters.latestAuditSubmissionFilterOptions.sort((a, b) => (a.name > b.name ? 1 : -1));
        filters.superVisionFrequencyOptions.sort((a, b) => (a.name > b.name ? 1 : -1));
        filters.superVisionTypeOptions.sort((a, b) => (a.name > b.name ? 1 : -1));
    }
}
